import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  DialogTrigger,
  Dialog,
  Modal as RACModal,
  ModalOverlay,
  SearchField,
  Input,
  Button as RACButton,
} from "react-aria-components";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type SortDescriptor,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import {
  Loader as LoaderIcon,
  Search as SearchIcon,
  UploadCloud,
  X,
} from "react-feather";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

import styles from "./locations.module.css";

/**
 * ISS's /slots/list has no substring search on slot_id (only an exact
 * match), unlike /items/list's item_desc filter -- see ListSlots in the
 * generated ISS client. So the whole catalog (up to ISS's own max page size)
 * is fetched once per zone filter and the free-text search happens
 * client-side, the same way the design's live-filtering search box behaves.
 */
const FETCH_LIMIT = 500;
const PAGE_SIZE = 50;
const ALL_ZONES_ID = "";

type LocationsSearch = {
  q: string;
  zone: string;
  offset: number;
};

type Zone = {
  id: number;
  name: string;
};

type SlotRow = {
  slot_id: string;
  zone_id?: number | null;
  warehouse_dept?: string | null;
  aisle?: string | null;
  section_type?: string | null;
  rack_level?: number | null;
  side?: number | null;
  handstack?: string | null;
  pick_sequence: number;
  slot_status_code?: string | null;
};

type ImportRowError = { row: number; column?: string; message: string };

type ImportOutcome =
  | {
      kind: "success";
      imported: number;
      errorCount: number;
      rowErrors: ImportRowError[];
    }
  | { kind: "failed"; message: string };

function parseOffset(value: unknown): number {
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : 0;
}

/**
 * openapi-react-query throws the parsed ErrorResponse body itself
 * (`{ error, message }`) rather than an `Error` -- see the analogous helper
 * in items.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/locations",
)({
  validateSearch: (search?: Record<string, unknown>): LocationsSearch => ({
    q: typeof search?.["q"] === "string" ? search["q"] : "",
    zone: typeof search?.["zone"] === "string" ? search["zone"] : "",
    offset: parseOffset(search?.["offset"]),
  }),
  component: RouteComponent,
});

function LocationTableRow({
  row,
  zoneName,
  onOpen,
}: {
  row: SlotRow;
  zoneName: string | null;
  onOpen: (row: SlotRow) => void;
}) {
  return (
    <TableRow data-test-role="location-row" onAction={() => onOpen(row)}>
      <TableCell>
        <span className={styles.slotId}>{row.slot_id}</span>
      </TableCell>
      <TableCell>
        {zoneName ? (
          <Tag label={zoneName} display={{ tone: "auto" }} />
        ) : (
          <span className={styles.noZone}>—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function ImportModal({
  onImport,
  isPending,
  t,
}: {
  onImport: (file: File) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <DialogTrigger>
      <RACButton className={styles.importTrigger}>
        <UploadCloud size={15} aria-hidden={true} />
        {t("locations.import.button")}
      </RACButton>
      <ModalOverlay isDismissable={true} className={styles.modalOverlay}>
        <RACModal className={styles.modal}>
          <Dialog className={styles.dialog}>
            {({ close }) => (
              <>
                <div className={styles.modalHeader}>
                  <strong>{t("locations.import.modalTitle")}</strong>
                  <RACButton
                    className={styles.modalDismiss}
                    aria-label={t("locations.drawer.close")}
                    onPress={() => {
                      setFile(null);
                      close();
                    }}
                  >
                    <X size={16} aria-hidden={true} />
                  </RACButton>
                </div>

                <label
                  className={styles.dropzone}
                  data-drag-over={isDragOver}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragOver(false);
                    const dropped = event.dataTransfer.files?.[0];
                    if (dropped) setFile(dropped);
                  }}
                >
                  <input
                    type="file"
                    accept=".csv"
                    className={styles.dropzoneInput}
                    onChange={(event) =>
                      setFile(event.target.files?.[0] ?? null)
                    }
                  />
                  <UploadCloud size={28} aria-hidden={true} />
                  <strong>
                    {file ? file.name : t("locations.import.chooseFile")}
                  </strong>
                  <span>{t("locations.import.dropHint")}</span>
                </label>

                <div className={styles.modalFooter}>
                  <Button
                    variant="secondary"
                    text={t("locations.import.cancel")}
                    isDisabled={isPending}
                    onPress={() => {
                      setFile(null);
                      close();
                    }}
                  />
                  <span className={styles.uploadAction}>
                    {isPending ? (
                      <LoaderIcon
                        size={15}
                        className={styles.uploadSpinner}
                        aria-hidden={true}
                      />
                    ) : null}
                    <Button
                      className={styles.uploadButton}
                      text={
                        isPending
                          ? t("locations.import.importing")
                          : t("locations.import.upload")
                      }
                      isDisabled={!file || isPending}
                      onPress={() => {
                        if (!file) return;
                        onImport(file).then(() => {
                          setFile(null);
                          close();
                        });
                      }}
                    />
                  </span>
                </div>
              </>
            )}
          </Dialog>
        </RACModal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

function SlotDrawer({
  row,
  zoneName,
  onClose,
  t,
}: {
  row: SlotRow;
  zoneName: string | null;
  onClose: () => void;
  t: TFunction;
}) {
  const none = t("locations.drawer.none");

  return (
    <div
      className={styles.drawerBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.drawer}
        role="dialog"
        aria-label={row.slot_id}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <div>
            <h2>{row.slot_id}</h2>
            <span>
              {[zoneName, row.section_type].filter(Boolean).join(" · ") || none}
            </span>
          </div>
          <button
            type="button"
            className={styles.drawerClose}
            aria-label={t("locations.drawer.close")}
            onClick={onClose}
          >
            <X size={18} aria-hidden={true} />
          </button>
        </div>

        <div className={styles.drawerSection}>
          <div className={styles.drawerSectionHeading}>
            {t("locations.drawer.slotAttributes")}
          </div>
          <dl className={styles.drawerGrid}>
            <div>
              <dt>{t("locations.table.slot")}</dt>
              <dd>{row.slot_id}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.zone")}</dt>
              <dd>{zoneName ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.locationType")}</dt>
              <dd>{row.section_type ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.warehouseDept")}</dt>
              <dd>{row.warehouse_dept ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.aisle")}</dt>
              <dd>{row.aisle ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.rackLevel")}</dt>
              <dd>{row.rack_level ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.side")}</dt>
              <dd>{row.side ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.handstack")}</dt>
              <dd>{row.handstack ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.pickSequence")}</dt>
              <dd>{row.pick_sequence}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.status")}</dt>
              <dd>{row.slot_status_code ?? none}</dd>
            </div>
          </dl>
        </div>

        <div className={styles.drawerFooter}>
          <span>{t("locations.drawer.readOnlyNotice")}</span>
          <Button
            variant="secondary"
            text={t("locations.drawer.edit")}
            isDisabled={true}
          />
        </div>
      </div>
    </div>
  );
}

function ImportBanner({
  outcome,
  onDismiss,
  t,
}: {
  outcome: ImportOutcome;
  onDismiss: () => void;
  t: TFunction;
}) {
  if (outcome.kind === "failed") {
    return (
      <div className={styles.importBanner} data-tone="danger">
        <span>
          {t("locations.import.failed", { message: outcome.message })}
        </span>
        <button
          type="button"
          className={styles.bannerDismiss}
          onClick={onDismiss}
        >
          {t("locations.import.dismiss")}
        </button>
      </div>
    );
  }

  return (
    <div
      className={styles.importBanner}
      data-tone={outcome.errorCount > 0 ? "warning" : "success"}
    >
      <div className={styles.importBannerSummary}>
        <span>
          {t("locations.import.successCount", { count: outcome.imported })}
        </span>
        {outcome.errorCount > 0 ? (
          <span>
            {t("locations.import.errorCount", { count: outcome.errorCount })}
          </span>
        ) : null}
        <button
          type="button"
          className={styles.bannerDismiss}
          onClick={onDismiss}
        >
          {t("locations.import.dismiss")}
        </button>
      </div>
      {outcome.rowErrors.length > 0 ? (
        <div className={styles.importErrors}>
          <strong>{t("locations.import.errorsHeading")}</strong>
          <ul>
            {outcome.rowErrors.map((rowError, index) => (
              <li key={`${rowError.row}-${rowError.column ?? index}`}>
                {t("locations.import.rowError", {
                  row: rowError.row,
                  message: rowError.column
                    ? `${rowError.column}: ${rowError.message}`
                    : rowError.message,
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useMemo(() => apiClient(), []);
  const [importOutcome, setImportOutcome] = useState<ImportOutcome | null>(
    null,
  );
  const [openRow, setOpenRow] = useState<SlotRow | null>(null);
  const [sortDir, setSortDir] = useState<"ascending" | "descending">(
    "ascending",
  );

  const goTo = (next: Partial<LocationsSearch>) => {
    navigate({
      search: (prev: LocationsSearch) => ({ ...prev, offset: 0, ...next }),
      resetScroll: false,
    });
  };

  const zonesQuery = client.useQuery("get", "/zones/all", {});
  const slotsQuery = client.useQuery(
    "get",
    "/slots/list",
    {
      params: {
        query: {
          limit: FETCH_LIMIT,
          offset: 0,
          ...(search.zone ? { zoneName: search.zone } : {}),
        },
      },
    },
    { placeholderData: (previousData) => previousData },
  );

  const importMutation = client.useMutation("post", "/slots/import");

  const zones = (zonesQuery.data?.data ?? []) as Zone[];
  const zoneNameById = new Map(zones.map((zone) => [zone.id, zone.name]));
  const slots = (slotsQuery.data?.data ?? []) as SlotRow[];

  const trimmedQuery = search.q.trim().toLowerCase();
  const filtered = trimmedQuery
    ? slots.filter((slot) => slot.slot_id.toLowerCase().includes(trimmedQuery))
    : slots;

  const sorted = [...filtered].sort((a, b) =>
    sortDir === "ascending"
      ? a.slot_id.localeCompare(b.slot_id)
      : b.slot_id.localeCompare(a.slot_id),
  );

  const total = sorted.length;
  const pageRows = sorted.slice(search.offset, search.offset + PAGE_SIZE);
  const currentPage = Math.floor(search.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total > 0 ? search.offset + 1 : 0;
  const rangeEnd = total > 0 ? search.offset + pageRows.length : 0;

  const totalOnServer = slotsQuery.data?.pagination.total_count ?? 0;
  const isTruncated = totalOnServer > slots.length;

  const sortDescriptor: SortDescriptor = {
    column: "slot_id",
    direction: sortDir,
  };

  const zoneItems = [
    { id: ALL_ZONES_ID, label: t("locations.filters.allZones") },
    ...zones.map((zone) => ({ id: zone.name, label: zone.name })),
  ];

  const isLoading = slotsQuery.isLoading || zonesQuery.isLoading;
  const isError = slotsQuery.isError || zonesQuery.isError;

  const handleImport = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return importMutation
      .mutateAsync({ body: formData as unknown as { file: string } })
      .then((response) => {
        setImportOutcome({
          kind: "success",
          imported: response.imported_count,
          errorCount: response.error_count,
          rowErrors: response.errors ?? [],
        });
        slotsQuery.refetch();
      })
      .catch((error: unknown) => {
        setImportOutcome({
          kind: "failed",
          message: errorMessage(error, t("locations.import.dismiss")),
        });
      });
  };

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <div className={styles.titleRow}>
            <h1>{t("locations.heading")}</h1>
            <div className={styles.toolbar}>
              <SearchField
                aria-label={t("locations.filters.searchAriaLabel")}
                className={styles.searchField}
                value={search.q}
                onChange={(value) => goTo({ q: value })}
              >
                <SearchIcon
                  size={15}
                  className={styles.searchIcon}
                  aria-hidden={true}
                />
                <Input placeholder={t("locations.filters.searchPlaceholder")} />
                {search.q ? (
                  <RACButton
                    className={styles.searchClear}
                    onPress={() => goTo({ q: "" })}
                  >
                    <X size={13} aria-hidden={true} />
                  </RACButton>
                ) : null}
              </SearchField>

              <Select
                name="zone"
                aria-label={t("locations.filters.zoneAriaLabel")}
                className={styles.zoneFilter}
                items={zoneItems}
                selectedId={search.zone || ALL_ZONES_ID}
                onSelectionChange={(key) =>
                  goTo({ zone: key ? String(key) : "" })
                }
              />

              <ImportModal
                onImport={handleImport}
                isPending={importMutation.isPending}
                t={t}
              />
            </div>
          </div>
          <p>{t("locations.subheading")}</p>
        </header>

        {importOutcome ? (
          <ImportBanner
            outcome={importOutcome}
            onDismiss={() => setImportOutcome(null)}
            t={t}
          />
        ) : null}

        {!isLoading && !isError && isTruncated ? (
          <div className={styles.truncatedNotice}>
            {t("locations.truncatedNotice", {
              shown: slots.length,
              total: totalOnServer,
            })}
          </div>
        ) : null}

        <div className={styles.tableWrap}>
          {isLoading ? (
            <Loader type="page" />
          ) : isError ? (
            <AppError
              description={t("locations.failed")}
              resolution={t("locations.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("locations.table.ariaLabel")}
              isResizable={false}
              sortDescriptor={sortDescriptor}
              onSortChange={(descriptor) =>
                setSortDir(
                  descriptor.direction === "descending"
                    ? "descending"
                    : "ascending",
                )
              }
            >
              <TableHead>
                <TableHeaderCell
                  id="slot_id"
                  isRowHeader={true}
                  isSortable={true}
                >
                  {t("locations.table.slot")}
                </TableHeaderCell>
                <TableHeaderCell>{t("locations.table.zone")}</TableHeaderCell>
              </TableHead>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <EmptyState heading={t("locations.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((row) => (
                    <LocationTableRow
                      key={row.slot_id}
                      row={row}
                      zoneName={
                        row.zone_id != null
                          ? (zoneNameById.get(row.zone_id) ?? null)
                          : null
                      }
                      onOpen={setOpenRow}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {!isLoading && !isError && total > 0 ? (
          <div className={styles.tableFooter}>
            <span className={styles.rangeSummary}>
              {t("locations.rangeSummary", {
                start: rangeStart,
                end: rangeEnd,
                total,
              })}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              className={palletOverrides.basicPagePagination}
              nextButton={{
                isDisabled: search.offset + pageRows.length >= total,
                onPress: () =>
                  navigate({
                    search: (prev: LocationsSearch) => ({
                      ...prev,
                      offset: prev.offset + PAGE_SIZE,
                    }),
                  }),
              }}
              previousButton={{
                isDisabled: search.offset <= 0,
                onPress: () =>
                  navigate({
                    search: (prev: LocationsSearch) => ({
                      ...prev,
                      offset: Math.max(0, prev.offset - PAGE_SIZE),
                    }),
                  }),
              }}
              jumpTo={(page) =>
                navigate({
                  search: (prev: LocationsSearch) => ({
                    ...prev,
                    offset: Math.max(0, (page - 1) * PAGE_SIZE),
                  }),
                })
              }
            />
          </div>
        ) : null}
      </div>

      {openRow ? (
        <SlotDrawer
          row={openRow}
          zoneName={
            openRow.zone_id != null
              ? (zoneNameById.get(openRow.zone_id) ?? null)
              : null
          }
          onClose={() => setOpenRow(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}
